import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
// @ts-ignore
import { ArrowUp, ArrowDown, Table as NbUiTable } from '@netbasequid/core'
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Row,
  useReactTable
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { getCellIndexOffsetByColDefs } from 'components/visualization/Table'
import { heapTrack, HeapEvent } from 'utils/heapUtils'
import { useDisplayTheme } from '../../../hooks/useUiState'
import { ColDefs, HeaderProps, TableContext, CellProps, FooterProps } from './types'

import CellRenderer from './CellRenderer'
import FooterRenderer from './FooterRenderer'
import { Theme } from '../../../features/ui/types'
import { AutomationId } from '../../../utils/automationUtil'

export type LongListTableProps<TRow, TContext, TField extends keyof TRow> = {
  rowData: TRow[]
  context?: TContext
  colDefs: ColDefs<TRow, TContext, TField>
  isDisplayFoot: boolean
  onRowClick?: (row: TRow) => void
  estimateRowHeight?: number
  overscan?: number
}

export interface LongListColDef<TRow, TContext, TField extends keyof TRow = keyof TRow> {
  field: TField
  headerText?: string | React.ReactNode
  customHeaderRender?: (context: TableContext<TContext>) => React.ReactNode
  headerProps?: HeaderProps
  customCellRender?: (
    cellValue: TRow[TField],
    context: TableContext<TContext, TRow>
  ) => React.ReactNode
  cellProps?: CellProps<TRow[TField], TableContext<TContext, TRow>>
  customFooterRender?: (context: TableContext<TContext>) => React.ReactNode
  footerProps?: FooterProps
  children?: Pick<LongListColDef<TRow, TContext, TField>, 'customCellRender' | 'cellProps'>[]
  accessorKey?: string
  sortable?: boolean
  sortingFn?: (rowA: Row<TRow>, rowB: Row<TRow>, columnId: string) => number
}

export type LongListColDefs<
  TRow,
  TContext = undefined,
  TField = keyof TRow
> = (TField extends keyof TRow ? LongListColDef<TRow, TContext, TField> : never)[]

const StyledHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: ${({ isSortable }: { isSortable?: boolean }) => (isSortable ? 'pointer' : 'default')};
`

const StyledTableContainer = styled.div`
  height: 100vh;
  overflow: auto;
  position: relative;
`

const LongListTable = <TRow, TContext, TField extends keyof TRow>({
  rowData,
  context,
  colDefs,
  isDisplayFoot,
  onRowClick,
  estimateRowHeight = 42,
  overscan = 5
}: LongListTableProps<TRow, TContext, TField>): React.ReactElement => {
  const theme = useDisplayTheme() as Theme
  const contextWithTheme: TableContext<any> = { ...context, theme }

  const cellIndexOffset = getCellIndexOffsetByColDefs<TRow, TContext, TField>(colDefs)

  const [sorting, setSorting] = useState<SortingState>([])
  const [isSorted, setIsSorted] = useState(false)

  const [data, setData] = useState(rowData)
  const [columns, setColumns] = useState<ColumnDef<TRow, any>[]>([])

  useEffect(() => {
    setData(rowData)
  }, [rowData])

  useEffect(() => {
    const mappedColumns: ColumnDef<TRow, any>[] = colDefs.map((colDef) => ({
      ...colDef,
      accessorKey: colDef.field,
      id: colDef.field as string
    }))
    setColumns(mappedColumns)
  }, [colDefs])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    onSortingChange: (newSorting: any) => {
      if (isSorted && newSorting(table.getState().sorting).length === 0) {
        setSorting([
          {
            ...table.getState().sorting[0],
            desc: !table.getState().sorting[0].desc
          }
        ])
      } else {
        setSorting(newSorting)
        setIsSorted(true)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  const { rows } = table.getRowModel()

  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateRowHeight,
    overscan
  })

  const headerPosition = virtualizer.scrollOffset

  const handleHeaderClick = (header: any) => {
    const sortingHandler = header.column.getToggleSortingHandler()

    return (event: React.MouseEvent) => {
      sortingHandler(event)
      heapTrack(HeapEvent.V4DashboardTableHeaderSelected, {
        sortBy: header.column.id,
        order: header.column.getIsSorted()
      })
    }
  }

  return (
    <StyledTableContainer ref={parentRef}>
      <NbUiTable
        mode={theme}
        height={`${virtualizer.getTotalSize()}px`}
        style={{
          maxHeight: 'none',
          overflow: 'visible'
        }}>
        <NbUiTable.TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <NbUiTable.TableRow key={headerGroup.id} style={{ position: 'static' }}>
              {headerGroup.headers.map((header) => {
                const colDef = colDefs.find((item) => item.field === header.id)
                return (
                  <NbUiTable.TableHeader
                    key={header.id}
                    onClick={handleHeaderClick(header)}
                    style={{
                      position: 'relative',
                      top: 0,
                      zIndex: 2000,
                      transition: 'transform 0.01s',
                      transform: !virtualizer.isScrolling
                        ? `translateY(${headerPosition}px)`
                        : 'translateY(0px)'
                    }}>
                    <StyledHeaderContainer isSortable={colDef?.sortable}>
                      {colDef?.customHeaderRender?.(contextWithTheme) || colDef?.headerText}
                      {colDef?.sortable && (
                        <>
                          {header.column.getIsSorted() === 'asc' && (
                            <ArrowUp fontSize={14} mode={theme} />
                          )}
                          {header.column.getIsSorted() === 'desc' && (
                            <ArrowDown fontSize={14} mode={theme} />
                          )}
                        </>
                      )}
                    </StyledHeaderContainer>
                  </NbUiTable.TableHeader>
                )
              })}
            </NbUiTable.TableRow>
          ))}
        </NbUiTable.TableHead>
        <NbUiTable.TableBody style={{ position: 'relative' }}>
          {virtualizer.getVirtualItems().map((virtualRow, index) => {
            const row = rows[virtualRow.index]
            return (
              <NbUiTable.TableRow
                data-index={virtualRow.index}
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={{
                  transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
                  overflow: 'hidden'
                }}>
                {row.getVisibleCells().map((cell, idx) => (
                  <CellRenderer
                    key={cell.id as React.Key}
                    colDef={colDefs[idx]}
                    context={{ row: row.original, ...contextWithTheme }}
                    indexOffset={cellIndexOffset[idx]}
                  />
                ))}
              </NbUiTable.TableRow>
            )
          })}
        </NbUiTable.TableBody>
        {isDisplayFoot && (
          <NbUiTable.TableFoot>
            <NbUiTable.TableRow>
              {colDefs.map((colDef, colIndex) => (
                <FooterRenderer
                  testId={`${AutomationId.table.footer}-${colIndex}`}
                  key={colDef.field as React.Key}
                  colDef={colDef}
                  context={contextWithTheme}
                />
              ))}
            </NbUiTable.TableRow>
          </NbUiTable.TableFoot>
        )}
      </NbUiTable>
    </StyledTableContainer>
  )
}

export default LongListTable
export * from './types/table'
